/**
 * Social media auto-sync: connections, drafts, and sync logs.
 * Uses Supabase client; approval flows create posts/events via database helpers.
 */

import { supabase, supabaseUrl, supabaseAnonKey } from './supabase';
import { createPost } from './database';

export const getSocialConnections = async (clubId) => {
  const { data, error } = await supabase
    .from('social_connections')
    .select('*')
    .eq('club_id', clubId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

export const disconnectSocialAccount = async (connectionId) => {
  const { error } = await supabase
    .from('social_connections')
    .delete()
    .eq('id', connectionId);

  if (error) throw error;
};

export const getSocialPostDrafts = async (clubId, status = 'pending_review') => {
  const { data, error } = await supabase
    .from('social_post_drafts')
    .select(`
      *,
      raw_post:social_raw_posts(id, caption, media_url, permalink, posted_at)
    `)
    .eq('club_id', clubId)
    .eq('status', status)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

export const getPendingDraftsCount = async (clubId) => {
  const { count, error } = await supabase
    .from('social_post_drafts')
    .select('*', { count: 'exact', head: true })
    .eq('club_id', clubId)
    .eq('status', 'pending_review');

  if (error) throw error;
  return count ?? 0;
};

export const updateDraft = async (draftId, updates) => {
  const { data, error } = await supabase
    .from('social_post_drafts')
    .update(updates)
    .eq('id', draftId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Approve draft as club announcement: creates post, then updates draft.
 * @param {string} draftId
 * @param {object} edits - { content?, is_member_only? }
 */
export const approveDraftAsAnnouncement = async (draftId, edits = {}) => {
  const { data: draft, error: draftErr } = await supabase
    .from('social_post_drafts')
    .select('club_id, content, title')
    .eq('id', draftId)
    .eq('status', 'pending_review')
    .single();

  if (draftErr || !draft) throw draftErr || new Error('Draft not found');

  const content = edits.content != null ? edits.content : (draft.content || draft.title || '');
  const post = await createPost({
    author_id: draft.club_id,
    content,
    is_member_only: edits.is_member_only ?? false,
  });

  const { error: updateErr } = await supabase
    .from('social_post_drafts')
    .update({
      status: 'approved',
      reviewed_at: new Date().toISOString(),
      created_post_id: post.id,
    })
    .eq('id', draftId);

  if (updateErr) throw updateErr;
  return post;
};

/**
 * Link draft to an event after the owner creates the event from CreateEventScreen.
 * Call this after createEvent when the event was created from a draft.
 * @param {string} draftId
 * @param {string} createdEventId
 */
export const approveDraftAsEvent = async (draftId, createdEventId) => {
  const { data, error } = await supabase
    .from('social_post_drafts')
    .update({
      status: 'approved',
      reviewed_at: new Date().toISOString(),
      created_event_id: createdEventId,
    })
    .eq('id', draftId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Reject/dismiss a draft.
 */
export const rejectDraft = async (draftId) => {
  const { data, error } = await supabase
    .from('social_post_drafts')
    .update({
      status: 'rejected',
      reviewed_at: new Date().toISOString(),
    })
    .eq('id', draftId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Fetch recent raw social posts for a club (for "Create event from post" flow).
 * Returns the most recent posts across all connected accounts and the platform label (e.g. 'instagram') when available.
 * @returns {{ posts: Array, platform?: string }}
 */
export const getRecentSocialPosts = async (clubId, limit = 10) => {
  const { data: connections, error: connErr } = await supabase
    .from('social_connections')
    .select('id, platform')
    .eq('club_id', clubId)
    .eq('is_active', true);

  if (connErr) throw connErr;
  if (!connections || connections.length === 0) {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'0bf1ae'},body:JSON.stringify({sessionId:'0bf1ae',location:'socialSyncService.js:getRecentSocialPosts',message:'no connections for club',data:{clubId,connectionsCount:0},timestamp:Date.now(),hypothesisId:'D'})}).catch(()=>{});
    // #endregion
    return { posts: [], platform: undefined };
  }

  const connectionIds = connections.map(c => c.id);
  const { data, error } = await supabase
    .from('social_raw_posts')
    .select('id, platform_post_id, caption, media_url, permalink, posted_at, post_type, connection_id, raw_payload')
    .in('connection_id', connectionIds)
    .order('posted_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  const result = data || [];
  const platform = connections[0]?.platform || undefined;
  // #region agent log
  fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'0bf1ae'},body:JSON.stringify({sessionId:'0bf1ae',location:'socialSyncService.js:getRecentSocialPosts',message:'posts result',data:{clubId,connectionsCount:connections.length,postsLength:result.length},timestamp:Date.now(),hypothesisId:'D'})}).catch(()=>{});
  // #endregion
  return { posts: result, platform };
};

export const getSyncLogs = async (connectionId, limit = 50) => {
  const { data, error } = await supabase
    .from('social_sync_logs')
    .select('*')
    .eq('connection_id', connectionId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data;
};

const functionsBase = `${supabaseUrl}/functions/v1`;

/**
 * Trigger sync for a single club's connected accounts (fetch recent posts from Meta).
 * Call this when a club with connected accounts loads the club dashboard.
 */
export const triggerSyncForClub = async (clubId) => {
  const res = await fetch(`${functionsBase}/social-sync-worker`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${supabaseAnonKey}`,
      apikey: supabaseAnonKey,
    },
    body: JSON.stringify({ clubId }),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(err || `Sync failed: ${res.status}`);
  }
  return res.json();
};

/**
 * Trigger parsing of pending raw posts for a single club (turn them into drafts).
 * Call after triggerSyncForClub so new posts become reviewable.
 */
export const triggerParseForClub = async (clubId) => {
  const res = await fetch(`${functionsBase}/social-content-parser`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${supabaseAnonKey}`,
      apikey: supabaseAnonKey,
    },
    body: JSON.stringify({ clubId }),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(err || `Parse failed: ${res.status}`);
  }
  return res.json();
};
